import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { reviewReportKeys, reviewTaskKeys } from "@/lib/query";

async function failure(res: Response, fallback: string): Promise<Error> {
  const json = (await res.json().catch(() => null)) as { error?: unknown } | null;
  return new Error(typeof json?.error === "string" && json.error ? json.error : fallback);
}

/**
 * One reviewer's answer, where the reader is entitled to see whose it is.
 *
 * The attribution fields are nullable because the server redacts them for a
 * reader who is not — see `reviewAudienceFor` in `src/api/review-report.ts`.
 * They are `| null` rather than optional so the compiler makes every render
 * site decide what to show, rather than quietly printing "undefined".
 */
export interface ReviewReportAnswer {
  reviewerId: string | null;
  relationshipKey: string | null;
  relationshipName: string | null;
  reviewerName: string | null;
  ordinal: number | null;
  rungName: string | null;
  comment: string | null;
  isSensitive: boolean;
}

/**
 * Reviewers the reader may not see individually, pooled by relationship.
 *
 * Always empty for a reader entitled to the whole review; for the subject it
 * carries peers, and direct reports once those are generated. Never fewer than
 * three people behind one, and never the individual scores inside it.
 */
export interface ReviewReportGroup {
  relationshipKey: string;
  relationshipName: string;
  count: number;
  /** Mean of the 1–5 ratings. Null when everyone who answered said N/A. */
  averageOrdinal: number | null;
  notApplicableCount: number;
  comments: string[];
}

export interface ReviewReport {
  reviewId: string;
  subjectPersonId: string;
  subjectName: string;
  roleName: string;
  roleLevelName: string | null;
  managerName: string | null;
  cycleName: string;
  cycleStatusKey: string;
  periodStart: string;
  periodEnd: string;
  statusKey: string;
  /** Sign-off — EPIC-065. `canSign*` already fold in eligibility and identity. */
  managerSignedByName: string | null;
  managerSignedAt: string | null;
  employeeSignedByName: string | null;
  employeeSignedAt: string | null;
  canSignAsManager: boolean;
  canSignAsEmployee: boolean;
  /** Whether this reader may tick written feedback through to the subject. */
  canShareFeedback: boolean;
  reviewers: {
    reviewerId: string | null;
    relationshipKey: string | null;
    relationshipName: string | null;
    reviewerName: string | null;
    statusKey: string;
    submittedAt: string | null;
    /** EPIC-057. Always null on the subject's own view. */
    excludedAt: string | null;
  }[];
  /**
   * Endorsements. Always shown to the subject — praise is the part of a review
   * people should read. Attribution follows the ratings rule: self and manager
   * named, everyone else only by relationship.
   */
  endorsements: {
    id: string;
    questionId: string;
    title: string;
    detail: string;
    endorserName: string | null;
    relationshipName: string | null;
  }[];
  /**
   * Written feedback, already filtered for this reader: a full reader gets
   * every note, the subject gets only the ones a manager released. Attribution
   * follows the same rule as ratings — self and manager named, everyone else
   * not — and an anonymous note has no author for anybody.
   */
  feedback: {
    id: string;
    body: string;
    sentimentKey: string;
    sentimentName: string;
    promptKey: string | null;
    isAnonymous: boolean;
    isVisibleToEmployee: boolean;
    authorName: string | null;
    relationshipName: string | null;
  }[];
  /**
   * Standalone feedback given since the subject's previous cycle closed —
   * EPIC-067 WI-247. Full readers only; always empty on the subject's own
   * redacted view.
   */
  adHocFeedback: {
    id: string;
    body: string;
    sentimentKey: string;
    sentimentName: string;
    isAnonymous: boolean;
    authorName: string | null;
    relatedProjectName: string | null;
    createdAt: string;
  }[];
  /** A manager's own prep for the conversation — EPIC-064. */
  managerNotes: {
    id: string;
    authorName: string;
    keepDoing: string | null;
    improve: string | null;
    goalsNextSteps: string | null;
    isDraft: boolean;
    updatedAt: string;
    isMine: boolean;
  }[];
  /** Whether THIS viewer may start or keep editing their own note. */
  canAddManagerNote: boolean;
  criteria: {
    questionId: string;
    /** The competency behind the question — the identity that is stable across cycles. */
    competencyId: string | null;
    /** What that competency is called, as opposed to how this form worded it. */
    competencyName: string | null;
    name: string;
    description: string | null;
    categoryName: string | null;
    categoryColor: string | null;
    meetsExpectation: string | null;
    answers: ReviewReportAnswer[];
    groups: ReviewReportGroup[];
  }[];
}

export function useReviewReport(reviewId: string | null) {
  return useQuery({
    queryKey: reviewReportKeys.detail(reviewId ?? ""),
    queryFn: async (): Promise<ReviewReport> => {
      const res = await fetch(`/api/reviews/${reviewId}`);
      if (!res.ok) {
        const json = (await res.json().catch(() => null)) as { error?: unknown } | null;
        throw new Error(
          typeof json?.error === "string" && json.error ? json.error : "Failed to load this review",
        );
      }
      return (await res.json()).data;
    },
    enabled: Boolean(reviewId),
  });
}

/** One confirmation that the review conversation happened — EPIC-065. */
export function useSignReviewOff(reviewId: string, party: "manager" | "employee") {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/reviews/${reviewId}/sign-off/${party}`, { method: "POST" });
      if (!res.ok) throw await failure(res, "Couldn't record your sign-off");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reviewReportKeys.detail(reviewId) });
      // The Reviews page lists who is still waiting on your sign-off.
      queryClient.invalidateQueries({ queryKey: reviewTaskKeys.teamResults });
      toast.success("Signed off");
    },
    onError: (error: Error) => toast.error(error.message),
  });
}

/** HR-only reversal (AC-676) — recorded, and not available to the signer themselves. */
export function useReverseReviewSignOff(reviewId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (party: "manager" | "employee") => {
      const res = await fetch(`/api/reviews/${reviewId}/sign-off/${party}`, { method: "DELETE" });
      if (!res.ok) throw await failure(res, "Couldn't reverse this sign-off");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reviewReportKeys.detail(reviewId) });
      toast.success("Sign-off reversed");
    },
    onError: (error: Error) => toast.error(error.message),
  });
}

/** Take a reviewer off a review, or bring them back — EPIC-057. */
export function useSetReviewerExcluded(reviewId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ reviewerId, excluded }: { reviewerId: string; excluded: boolean }) => {
      const res = await fetch(`/api/reviews/${reviewId}/reviewers/${reviewerId}/exclusion`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ excluded }),
      });
      if (!res.ok) throw await failure(res, "Couldn't change whether this reviewer counts");
      return res.json();
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: reviewReportKeys.detail(reviewId) });
      toast.success(variables.excluded ? "Reviewer excluded" : "Reviewer included again");
    },
    onError: (error: Error) => toast.error(error.message),
  });
}

/** Add a reviewer by hand — EPIC-059 WI-212. */
export function useAddReviewer(reviewId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { reviewerPersonId: string; relationshipKey: string }) => {
      const res = await fetch(`/api/reviews/${reviewId}/reviewers`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw await failure(res, "Couldn't add that reviewer");
      return (await res.json()).data as { reviewerId: string };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reviewReportKeys.detail(reviewId) });
      toast.success("Reviewer added");
    },
    onError: (error: Error) => toast.error(error.message),
  });
}

/** Re-label an existing reviewer's relationship — EPIC-059 WI-213. */
export function useSetReviewerRelationship(reviewId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      reviewerId,
      relationshipKey,
    }: {
      reviewerId: string;
      relationshipKey: string;
    }) => {
      const res = await fetch(`/api/reviews/${reviewId}/reviewers/${reviewerId}/relationship`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ relationshipKey }),
      });
      if (!res.ok) throw await failure(res, "Couldn't change their relationship");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reviewReportKeys.detail(reviewId) });
      toast.success("Relationship changed");
    },
    onError: (error: Error) => toast.error(error.message),
  });
}

/** Whether a reviewer's seeded questions still match their relationship — EPIC-059 WI-213. */
export interface ReviewerMismatch {
  hasMismatch: boolean;
  currentRelationshipName: string;
  answeredToDiscard: number;
  newQuestionCount: number;
}

export function useReviewerMismatch(reviewId: string, reviewerId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: [...reviewReportKeys.detail(reviewId), "mismatch", reviewerId],
    queryFn: async (): Promise<ReviewerMismatch> => {
      const res = await fetch(`/api/reviews/${reviewId}/reviewers/${reviewerId}/mismatch`);
      if (!res.ok) throw await failure(res, "Couldn't check this reviewer's questions");
      return (await res.json()).data;
    },
    enabled: enabled && Boolean(reviewerId),
  });
}

/** The explicit fix for a flagged mismatch — EPIC-059 WI-213. */
export function useRegenerateReviewerQuestions(reviewId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (reviewerId: string) => {
      const res = await fetch(`/api/reviews/${reviewId}/reviewers/${reviewerId}/regenerate`, {
        method: "POST",
      });
      if (!res.ok) throw await failure(res, "Couldn't regenerate this reviewer's questions");
      return (await res.json()).data as {
        carriedOver: number;
        discarded: number;
        newQuestionCount: number;
      };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: reviewReportKeys.detail(reviewId) });
      toast.success(
        data.discarded > 0
          ? `Questions regenerated — ${data.carriedOver} carried over, ${data.discarded} discarded`
          : "Questions regenerated",
      );
    },
    onError: (error: Error) => toast.error(error.message),
  });
}

/** The caller's own draft, as its three sections — EPIC-064. */
export interface ManagerNoteDraftInput {
  keepDoing: string | null;
  improve: string | null;
  goalsNextSteps: string | null;
}

/** Save the caller's own "Summary and Next Steps" draft — EPIC-064. */
export function useSaveManagerNoteDraft(reviewId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (sections: ManagerNoteDraftInput) => {
      const res = await fetch(`/api/reviews/${reviewId}/manager-notes`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(sections),
      });
      if (!res.ok) throw await failure(res, "Couldn't save your note");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reviewReportKeys.detail(reviewId) });
      toast.success("Draft saved");
    },
    onError: (error: Error) => toast.error(error.message),
  });
}

/** Make the caller's note visible to everyone entitled to the full review — a deliberate, separate step. */
export function useFinalizeManagerNote(reviewId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/reviews/${reviewId}/manager-notes/finalize`, {
        method: "POST",
      });
      if (!res.ok) throw await failure(res, "Couldn't finalize your note");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reviewReportKeys.detail(reviewId) });
      toast.success("Note finalized");
    },
    onError: (error: Error) => toast.error(error.message),
  });
}

/** Every review a person has ever had, in one place — EPIC-070. */
export interface PersonReviewHistoryEntry {
  reviewId: string;
  cycleName: string;
  cycleStatusKey: string;
  periodStart: string;
  periodEnd: string;
  report: ReviewReport;
}

export function usePersonReviewHistory(personId: string, enabled: boolean) {
  return useQuery({
    queryKey: reviewReportKeys.personHistory(personId),
    queryFn: async (): Promise<PersonReviewHistoryEntry[]> => {
      const res = await fetch(`/api/persons/${personId}/review-history`);
      if (!res.ok) throw await failure(res, "Failed to load review history");
      return (await res.json()).data;
    },
    enabled,
  });
}
